import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { SIGNUP_PATH } from './seoPages';
import { EFFY_FUEL_PRODUCT, FUEL_URL } from '../products/effyFuelContent';
import { FuelCta } from '../products/FuelCta';
import { buildSoftwareApplicationSchema, buildEffyFuelSchema } from './seoSchemas';
import { SeoHead, ProductBreadcrumb, CheckIcon, FaqSection, RelatedLinks, SignupCta, GuideArticle } from './SeoShared';
import '../products/EffyOrder.css';
import './SeoPages.css';

// One keyword landing page (e.g. /gst-billing-software), rendered from a
// seoPages.js entry. Reuses the Effy Order product page's hero/feature styles
// so every landing page looks like the rest of the marketing site.
export default function SeoLandingPage({ page }) {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [page.path]);

  // Effy Fuel pages sign up on the fuel app; every other page signs up for Effy Order.
  const isFuelPage = page.product === EFFY_FUEL_PRODUCT;
  let primaryButton = <Link to={SIGNUP_PATH} className="btn-v2 btn-v2--primary">Try it free</Link>;
  let closingCta = <SignupCta />;
  if (isFuelPage) {
    primaryButton = <a href={FUEL_URL + '/signup'} className="btn-v2 btn-v2--primary">Try it free</a>;
    closingCta = <FuelCta />;
  }

  let productSchema = buildSoftwareApplicationSchema(page);
  if (isFuelPage) {
    productSchema = buildEffyFuelSchema();
  }

  return (
    <div className="oi-page-v2">
      <SeoHead
        path={page.path}
        title={page.metaTitle}
        description={page.metaDescription}
        breadcrumbName={page.badge}
        product={page.product}
        faqs={page.faqs}
        extraSchema={productSchema}
      />

      <section className="oi-hero-v2">
        <div className="oi-hero-v2__container">
          <div className="oi-hero-v2__content">
            <ProductBreadcrumb product={page.product} current={page.badge} />
            <div className="oi-badge-v2">{page.product.name} · {page.badge}</div>
            <h1 className="oi-hero-v2__title">
              {page.heading} <span className="text-gradient">{page.headingHighlight}</span>
            </h1>
            <p className="oi-hero-v2__subtitle">{page.subtitle}</p>
            <ul className="oi-hero-v2__features-list">
              {page.heroPoints.map((point) => (
                <li key={point}><span>✔</span> {point}</li>
              ))}
            </ul>
            <div className="oi-hero-v2__actions">
              {primaryButton}
              <Link to={page.product.featuresPath} className="btn-v2 btn-v2--secondary">See all {page.product.name} features</Link>
            </div>
          </div>
          <div className="oi-hero-v2__visual">
            <div className="oi-hero-v2__image-wrapper">
              <img src={page.image} alt={page.imageAlt} className="oi-hero-v2__img seo-hero__img" />
            </div>
          </div>
        </div>
      </section>

      <GuideArticle guide={page.guide} />

      <div className="oi-features-wrapper">
        {page.sections.map((section) => (
          <section key={section.title} className="oi-feature-v2 seo-feature">
            <div className="seo-feature__container">
              <span className="oi-feature-v2__label">{section.label}</span>
              <h2 className="oi-feature-v2__title">{section.title}</h2>
              <p className="oi-feature-v2__desc">{section.description}</p>
              <ul className="oi-feature-v2__list seo-feature__list">
                {section.points.map((point) => (
                  <li key={point}>
                    <CheckIcon />
                    <span>{point}</span>
                  </li>
                ))}
              </ul>
            </div>
          </section>
        ))}
      </div>

      <FaqSection faqs={page.faqs} />
      <RelatedLinks paths={page.related} />
      {closingCta}
    </div>
  );
}
